import type { EditorView } from 'prosemirror-view';

import type { HighlightSchema } from './highlight-mark';
import type { HighlightMenuController } from './highlight-plugin.types';
import type { EditorButton, EditorMode } from '../../editor/types';
import { MarkService } from '../../helpers/mark-base';

export class HighlightService<S extends HighlightSchema> extends MarkService<S> {}

export class HighlightPlugin {
  public api: {
    highlightService: HighlightService<HighlightSchema>;
  };

  public buttons = {
    toggleHighlight: <EditorButton>{
      icon: 'format-color-fill',
      tooltipText: 'wysiwyg.highlight',
      isActive: (state, ...args) =>
        this.menuController.isOpen() || this.api.highlightService.isActive(state),
      run: (state, dispatch, origin) => {
        const highlightChanged = (value: string | null) => {
          if (value) {
            this.api.highlightService.add(this.editorView.state, dispatch, { hex: value });
          } else {
            this.api.highlightService.remove(this.editorView.state, dispatch);
          }
        };
        const current = this.api.highlightService.getAttrsIfActive(state);
        const { closed$ } = this.menuController.open(origin.getBoundingClientRect(), {
          parent: origin,
          onChange: highlightChanged,
          initialValue: current ? current.hex : null,
        });
        closed$.subscribe(({ value }) => {
          if (!value) {
            this.api.highlightService.remove(state, dispatch);
          }
          this.editorView.focus();
        });
      },
    },
  };

  private editorView: EditorView;

  constructor(protected menuController: HighlightMenuController) {}

  public beforeInit(mode: EditorMode, schema: HighlightSchema) {
    this.api = {
      highlightService: new HighlightService(schema.marks.background_color),
    };
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }
}
